<script setup lang="ts">
import { ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import SwiftaidAuthorisationPanel from '@/components/dashboard/authorisation/SwiftaidAuthorisationPanel.vue'
import { LANDING_PAGE } from '@/types'
import { scrollWindowToTopSoon } from '@/helpers/scrollWindowToTop'

const props = withDefaults(
  defineProps<{
    compact?: boolean
    termsInputId?: string
    showSubmit?: boolean
    /** When true, navigate to the dashboard after Agree (signup declaration). */
    navigateToDashboardOnSuccess?: boolean
    /** Hide the panel heading when the parent page already titles the step. */
    hidePanelTitle?: boolean
  }>(),
  {
    compact: false,
    termsInputId: 'renew-terms',
    showSubmit: true,
    navigateToDashboardOnSuccess: false,
    hidePanelTitle: false,
  }
)

const termsAgreed = defineModel<boolean>('termsAgreed', { default: false })

const emit = defineEmits<{
  success: []
}>()

const store = useStore()
const router = useRouter()
const btnLoading = ref(false)

const submitAuthorisation = async () => {
  if (!termsAgreed.value || btnLoading.value) {
    return
  }

  btnLoading.value = true
  try {
    await store.dispatch('postAuth')
    // Navigate here (not only via @success) so Agree still completes if a parent
    // remounts or the success listener is dropped mid-request.
    if (props.navigateToDashboardOnSuccess) {
      store.commit('SET_LANDING_PAGE', LANDING_PAGE.DASHBOARD)
      store.commit('SET_AUTH_INTENT', 'login')
      await router.replace({ name: 'Dashboard' })
      // replace() keeps the same history entry — without a forced reset the
      // browser can leave the scrolled declaration offset on the new layout.
      scrollWindowToTopSoon()
    }
    emit('success')
  } catch (error) {
    console.error('Failed to reactivate Swiftaid authorisation', error)
  } finally {
    btnLoading.value = false
  }
}

const reset = () => {
  termsAgreed.value = false
  btnLoading.value = false
}

defineExpose({
  btnLoading,
  submitAuthorisation,
  reset,
})
</script>

<template>
  <SwiftaidAuthorisationPanel
    v-model:terms-agreed="termsAgreed"
    :compact="compact"
    :terms-input-id="termsInputId"
    :hide-title="hidePanelTitle"
  />

  <div v-if="showSubmit" class="sa-renew-flow__actions">
    <button
      id="agree"
      type="button"
      class="sa-btn-primary sa-auth-form__submit"
      :aria-label="$t('Dashboard.Status.Authorisation.ConfirmAria')"
      :disabled="!termsAgreed || btnLoading"
      :aria-busy="btnLoading"
      @click.prevent="submitAuthorisation"
    >
      <span
        v-if="btnLoading"
        class="spinner sa-btn-spinner"
        aria-hidden="true"
      />
      <span>{{ $t('Buttons.Agree') }}</span>
    </button>
  </div>
</template>
